import React, { useState, useEffect } from 'react';
import { Phone, Star, Clock, MapPin, CheckCircle, Wrench, Shield, MessageCircle, Zap, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const HomePage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const phoneNumber = 'tel:+61861180083';
  const displayPhone = '(08) 6118 0083';

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const services = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'General Car Servicing',
      description: 'Complete vehicle servicing to keep your car running smoothly and safely.'
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Car Repairs',
      description: 'Expert diagnosis and repair of all vehicle issues, big or small.'
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Engine Repairs',
      description: 'Specialist engine diagnostics and repairs by qualified mechanics.'
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Vehicle Inspections',
      description: 'Thorough pre-purchase and safety inspections you can trust.'
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: 'Mobile Service',
      description: 'We come to your home or workplace across Perth metro.'
    }
  ];

  const whyChooseUs = [
    'Honest mechanic — no upselling repairs you don\'t need',
    'Clear communication — keeps you updated by phone',
    'Affordable, fair pricing',
    'Fast and efficient service',
    'Friendly, professional customer service',
    'Convenient mobile service across Perth'
  ];

  const reviews = [
    'Reliable, efficient, honest and gets the work done at such a good price.',
    'Repaired my engine in one day and it\'s been running great ever since.',
    'Great communication… kept me up to date via phone and helped me understand what they\'d done.',
    'Did not try to sell additional repair… fixed all the problems I wanted… price was reasonable.',
    'Excellent professional communication, best price and the service was amazing.',
    'Perth\'s best vehicle inspection service.',
    'Very fast and efficient. Great service, really nice person.',
    'Had my car serviced at my work.'
  ];

  const trustPoints = [
    { icon: <Star className="w-6 h-6" />, text: '5.0 ★ (11 Google reviews)' },
    { icon: <Shield className="w-6 h-6" />, text: 'Honest & transparent pricing' },
    { icon: <CheckCircle className="w-6 h-6" />, text: 'No unnecessary repairs' },
    { icon: <Zap className="w-6 h-6" />, text: 'Fast & reliable service' },
    { icon: <MessageCircle className="w-6 h-6" />, text: 'Great communication' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-lg font-bold text-gray-900">The Perth Mobile Mechanics</h1>
                <div className="flex items-center gap-1 text-sm text-yellow-500">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-gray-900 font-semibold">5.0</span>
                  <span className="text-gray-600">(11 reviews)</span>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-600 transition-colors">Home</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors">Services</button>
              <button onClick={() => scrollToSection('reviews')} className="text-gray-700 hover:text-blue-600 transition-colors">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors">Contact</button>
              <a href={phoneNumber} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors">
                <Phone className="w-4 h-4" />
                {displayPhone}
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700 hover:text-blue-600 transition-colors"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">Home</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">Services</button>
              <button onClick={() => scrollToSection('reviews')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-gray-700 hover:text-blue-600 transition-colors py-2">Contact</button>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-16 px-4 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Mobile Mechanic in Perth – We Come To You
              </h2>
              <p className="text-xl text-gray-700 mb-8">
                Fast, honest and affordable car repairs with great communication and no pushy upsells.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a href={phoneNumber} className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105 shadow-lg">
                  <Phone className="w-5 h-5" />
                  Call {displayPhone}
                </a>
                <a href={phoneNumber} className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105">
                  Get a Quote
                </a>
              </div>

              <div className="space-y-3 text-gray-700">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  <span>Servicing Perth metro area</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>Mon–Fri: 7am–7pm | Sat–Sun: 9am–4pm</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-500 fill-current" />
                  <span className="font-semibold">5.0 ★ Google rating</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80"
                alt="Mobile mechanic working on car"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {trustPoints.map((point, index) => (
              <div key={index} className="flex flex-col items-center text-center gap-2">
                <div className="text-blue-600">{point.icon}</div>
                <p className="text-sm font-medium text-gray-900">{point.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-700">Professional mobile mechanic services across Perth</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="border-2 hover:border-blue-600 transition-all hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="text-blue-600 mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-700">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-blue-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {whyChooseUs.map((point, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-900">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="w-8 h-8 text-yellow-500 fill-current" />
              <span className="text-4xl font-bold text-gray-900">5.0</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Customer Reviews</h2>
            <p className="text-xl text-gray-700">11 Google Reviews</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <Card key={index} className="border hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 italic">"{review}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-b from-blue-50 to-blue-100">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get a Quote</h2>
          <p className="text-xl text-gray-700 mb-8">
            Need a quote or car repair? Call and speak directly with a qualified mobile mechanic.
          </p>

          <a href={phoneNumber} className="inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 text-white px-12 py-6 rounded-lg font-bold text-2xl transition-all hover:scale-105 shadow-2xl mb-8">
            <Phone className="w-7 h-7" />
            {displayPhone}
          </a>

          <div className="grid md:grid-cols-2 gap-4 mb-8 text-left max-w-2xl mx-auto">
            <div className="flex items-center gap-2 text-gray-900">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Fast response</span>
            </div>
            <div className="flex items-center gap-2 text-gray-900">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>No call centres</span>
            </div>
            <div className="flex items-center gap-2 text-gray-900">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>No online forms</span>
            </div>
            <div className="flex items-center gap-2 text-gray-900">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span>Straightforward, honest advice</span>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-lg max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Opening Hours</h3>
            <div className="space-y-2 text-gray-700">
              <div className="flex justify-between">
                <span className="font-semibold">Monday – Friday:</span>
                <span>7am – 7pm</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Saturday:</span>
                <span>9am – 4pm</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Sunday:</span>
                <span>9am – 4pm</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-gray-900">
                Servicing Perth metro — we come to your home or workplace.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Wrench className="w-6 h-6" />
            <h3 className="text-xl font-bold">The Perth Mobile Mechanics</h3>
          </div>
          <p className="text-gray-400 mb-4">Mobile Mechanic in Perth – We Come To You</p>
          <p className="text-gray-400 mb-4">Mobile mechanic Perth | Car repairs Perth | Car servicing Perth | Vehicle inspections Perth | Affordable mechanic Perth | Honest mechanic Perth</p>
          <p className="text-gray-500 text-sm">© 2025 The Perth Mobile Mechanics. All rights reserved.</p>
        </div>
      </footer>

      {/* Sticky Mobile Call Button */}
      <a
        href={phoneNumber}
        className="md:hidden fixed bottom-6 right-6 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-4 rounded-full font-bold shadow-2xl transition-all hover:scale-110 z-40"
      >
        <Phone className="w-5 h-5" />
        Call Now
      </a>
    </div>
  );
};

export default HomePage;